// AnalyticModal.jsx
import { Formik, Form } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import FormikInput from "../Formik/FormikInput";
import { useLanguage } from "../LangTranslation/LanguageContext";
import useConfirmationModal from "../ConfirmationModal/useConfirmationModal";
import SVGButton from "../Buttons/SVGButton";
import LoaderWrapper from "../Loader/LoaderWrapper";
import useFormikSubmitButton from "../../CustomHooks/useFormikSubmitButton";
import { handleResponse } from "../../utilities/handleResponse";
import editIcon from "../../Images/edit.svg";

import { read } from "../../DataApi/read";
import { useSession } from "../../authentications/SessionContext";
import { write } from "../../DataApi/write";
import * as Yup from "yup";

const FormContainer = styled.div`
`;

const AnalyticModal = ({ handleCloseModal, onSuccess }) => {
  const { language, translations } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState("view");
  const [previousInputs, setPreviousInputs] = useState({});
  const { setIsSubmitting, FormikSubmitButtons } = useFormikSubmitButton();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const { ConfirmationModal, toggleModal, setMetaData, setTitle } =
    useConfirmationModal();

  const { sessionInfo } = useSession();
  const { company_id, context, current_website_id } = sessionInfo;

  const [formData, setFormData] = useState({
    ga4_property_id: "",
    ga4_tracking_key: "",
  });

  const validationSchema = Yup.object().shape({
    ga4_property_id: Yup.string().required("Property ID is required"),
    ga4_tracking_key: Yup.string().required("Tag ID is required"),
  });

  const getPayload = (payload) => ({
    ga4_property_id: payload?.ga4_property_id || "",
    ga4_tracking_key: payload?.ga4_tracking_key || "",
  });

  // Try fetching without ID if it's always a single config
  const fetchGADetail = async () => {
    setIsLoading(true);
    try {
      const response = await read({
        id: current_website_id,
        // change: [],
        context,
        company_id,
        model: "website",
      });

      const result = JSON.parse(response)?.result?.[0];

      if (!result) {
        console.warn("No result found from API");
        return;
      }

      const inputList = {
        ga4_debug_mode: result?.ga4_debug_mode,
        ga4_property_id: result?.ga4_property_id,
        ga4_tracking_key: result?.ga4_tracking_key,
      };

      if (!inputList.ga4_property_id && !inputList.ga4_tracking_key) {
        setView("add");
      } else {
        setView("view");
      }

      setFormData({ ...inputList, id: result?.id }); 
      setPreviousInputs({ ...inputList });
    } catch (error) {
      console.error("Error fetching GA data:", error);
    } finally {
      setIsLoading(false);
    }
  };

const AddandEditGAForm = async (values) => {
  const payload = getPayload(values);
  const oldPayload = getPayload(previousInputs);
  const isEdit = view === "edit";
  const changes = {};

  if (isEdit) {
    Object.keys(payload).forEach((key) => {
      if (payload[key] !== oldPayload[key]) {
        changes[key] = payload[key];
      }
    });

    // No changes, skip write call
    if (Object.keys(changes).length === 0) {
      handleCloseModal();
      return null;
    }
  }

  const response = await write({
    id: formData.id || current_website_id, // Use existing ID or new one
    change: isEdit ? changes : payload,
    company_id,
    context,
    model: "website",
  });

  return JSON.parse(response);
};



const handleSubmit = async (values) => {
  setIsSubmitting(true);
  let response = {};
  let successMessage = "GA Configuration Saved";

  try {
    response = await AddandEditGAForm(values);
    if (!response?.result) return;

    toggleModal();
    handleResponse({
      response,
      setMetaData,
      setTitle,
      successMessage:
        view === "edit" ? "GA Edited Successfully" : "GA Configuration Saved",
    });

    onSuccess?.();
    handleCloseModal();
  } catch (error) {
    console.error("Submit Error:", error);
  } finally {
    setIsSubmitting(false);
    setIsLoadingSubmit(false);
  }
};

  useEffect(() => {
    if (current_website_id) {
      fetchGADetail();
    }
  }, [current_website_id]);
  const disableInputs = useMemo(() => view === "view", [view]);

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isSubmitting, values }) => (
        <Form>
          <div
            className="container"
            style={{ maxWidth: "800px", padding: "30px" }}
          >
            <FormContainer className="row g-3">
              <div className="row p-0 justify-content-between align-items-center">
                <div className="col">
                  <h3
                    className="me-2 mb-2 "
                    style={{
                      fontFamily: "Inter",
                      color: "#323B4B",
                      fontWeight: 600,
                    }}
                  >
                    {view === "edit" && "Google Analytic Form"}
                    {view === "view" && "Google Analytic Form"}
                    {view === "add" && "Add Google Analytics Configuration"}
                  </h3>
                </div>
                {view === "view" && (
                  <div className="col d-flex justify-content-end p-0">
                    <SVGButton
                      hoverColor={"#ff5837"}
                      icon={editIcon}
                      onClick={() => {
                        setView("edit");
                      }}
                      className="me-2"
                    />
                  </div>
                )}
              </div>

              <LoaderWrapper isLoading={isLoading} height="50vh">
                <div className="col-12">
                  <FormikInput
                    type="text"
                    name="ga4_property_id"
                    label="Property ID"
                    disabled={disableInputs}
                  />
                </div>

                <div className="col-md-12">
                  <FormikInput
                    type="text"
                    name="ga4_tracking_key"
                    label="Tag ID"
                    disabled={disableInputs}
                  />
                </div>

                {/* <div className="d-flex justify-content-end mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </button>
                </div> */}
                {!disableInputs && (
                  <FormikSubmitButtons handleCloseModal={handleCloseModal} />
                )}
              </LoaderWrapper>
            </FormContainer>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AnalyticModal;
